import _ from 'lodash';
import moment from 'moment';
import MoviesService from './movies.service';
import ImdbMovie from './imdb-movie.schema';


// inspired by imdb scraper found here:
// https://github.com/mhsjlw/imdb


export default class ImdbLegacyHtmlParser {

  static get ValidContentRatings() {
    return MoviesService.MPAARatingOptions;
  }

  imdbMovieId = null;
  doc = null;

  selectors = {
    title:          '#title-overview-widget > div.vital > div.title_block > div > div.titleBar > div.title_wrapper > h1',
    year:           '#titleYear > a',
    contentRating:  '#title-overview-widget > div.vital > div.title_block > div > div.titleBar > div.title_wrapper > div.subtext',
    runtime:        '#title-overview-widget > div.vital > div.title_block > div > div.titleBar > div.title_wrapper > div > time',
    description:    '#title-overview-widget > div.plot_summary_wrapper > div.plot_summary > div.summary_text',
    starRating:     '#title-overview-widget > div.vital > div.title_block > div > div.ratings_wrapper > div.imdbRating > div.ratingValue > strong > span',
    poster1:        '#title-overview-widget > div.vital > div.slate_wrapper > div.poster > a > img',
    poster2:        '#title-overview-widget > div.minPosterWithPlotSummaryHeight > div.poster > a > img',
    metascore:      '#title-overview-widget > div.plot_summary_wrapper > div.titleReviewBar .metacriticScore > span',
    cast:           '#title-overview-widget > div.plot_summary_wrapper > div.plot_summary > .credit_summary_item',
    writers:        '#title-overview-widget > div.plot_summary_wrapper > div.plot_summary > .credit_summary_item',
    directors:      '#title-overview-widget > div.plot_summary_wrapper > div.plot_summary > .credit_summary_item',
    genre:          '#title-overview-widget > div.vital > div.title_block > div > div.titleBar > div.title_wrapper > div.subtext',
    releaseDate:    '#title-overview-widget > div.vital > div.title_block > div > div.titleBar > div.title_wrapper > div.subtext',
  };

  constructor(doc, imdbMovieId = null) {
    this.imdbMovieId = imdbMovieId;
    this.doc = doc;
  }

  parseAll() {
    let movie = null;
    if (this.doc) {
      movie = new ImdbMovie();
      movie.imdb_id = this.imdbMovieId;
      movie.title = this.getTitle();
      movie.release_date = this.getReleaseDate();
      movie.release_year = this.getYear();

      let posterUrls = this.getPosterUrls();
      movie.imdb_img = _.first(posterUrls);
      movie.imdb_imgs = posterUrls;

      movie.rating = this.getContentRating();
      movie.duration_mins = this.getRuntimeMinutes();
      movie.synopsis = this.findInnerText(this.selectors.description);
      movie.star_rating = this.getStarRating();
      movie.metascore = this.getMetaScore();

      movie.all_genres = this.getGenres();
      movie.all_writers = this.getWriters();
      movie.all_directors = this.getDirectors();
      movie.all_cast = this.getCast();

      movie.genre = _.join(movie.all_genres, ', ');
      movie.cast = _.join(movie.all_cast, ', ');
    }
    return movie;
  }

  getTitle() {
    let str = this.findInnerText(this.selectors.title);
    if (str) {
      str = _.trim(_.replace(str, /\(\d+\)/g, ''));
    }
    return str;
  }

  getYear() {
    let str = this.findInnerText(this.selectors.year);
    return str? parseInt(str): null;
  }

  getReleaseDate() {
    let str = this.findInnerText(this.selectors.genre);
    let parts = _.chain(str)
      .trim()
      .split('|')
      .map(_.trim)
      .compact()
      .value();

    let part = _.nth(parts, -1);
    let tmpstr = _.chain(part)
      .trim()
      .replace(/\(.*/, '')
      .trim()
      .value();

    let datestr = null;
    if (!_.isEmpty(tmpstr)) {
      let tmpDate = null;
      try {
        tmpDate = moment(new Date(tmpstr));
      } catch (ex) {}
      if (tmpDate && tmpDate.isValid()) {
        datestr = tmpDate.format('YYYY-MM-DD');   // iso date only (no time)
      }
    }
    return datestr;
  }

  getPosterUrls() {
    let poster1 = this.findAttr(this.selectors.poster1, 'src');
    let poster2 = this.findAttr(this.selectors.poster2, 'src');
    let poster1ResizedA = _.replace(poster1, /\._[^.]+_(\.\w+)$/, '$1');    // hacked to grab resized media at 1000px tall.  Updated 12/14/2020 to account for new URL syntax
    // let poster1ResizedB = _.replace(poster1, /@\._V1.*\.(\w+)$/, '@._V1_SY1000_AL_.$1');    // hacked to grab resized media at 1000px tall

    return _.chain([ poster1ResizedA, poster1, poster2 ])
      .map(_.trim)
      .uniq()
      .compact()
      .value();
  }

  getRuntimeMinutes() {
    let mins = null;
    let duration = null;
    let str = this.findAttr(this.selectors.runtime, 'datetime');
    if (str) {
      duration = moment.duration(str);
      if (duration && duration.isValid()) {
        mins = duration.asMinutes();
      }
    }

    /*
    if (mins === null) {
      // TODO: joe- this is not parsing the human readable format
      str = this.findInnerText(this.selectors.runtime);
      duration = moment.duration(str);
      if (duration && duration.isValid()) {
        mins = duration.asMinutes();
      }
    } //*/

    return mins;
  }

  getCast() {
    let parts = this.findInnerTexts(this.selectors.cast);
    let items = _.chain(parts)
      .map(_.trim)
      .filter(v => { return _.startsWith(v, 'Star'); })
      .first()
      .replace(/^Stars?\s*:?/, '')
      .replace(/\|.*$/, '')
      .trim()
      .split(',')
      .map(_.trim)
      .uniq()
      .compact()
      .value();
    return !_.isEmpty(items)? items: null;
  }

  getWriters() {
    let parts = this.findInnerTexts(this.selectors.writers);
    let items = _.chain(parts)
      .map(_.trim)
      .filter(v => { return _.startsWith(v, 'Writer'); })
      .first()
      .replace(/^Writers?\s*:?/, '')
      .replace(/\|.*$/, '')
      .trim()
      .split(',')
      .map(_.trim)
      .uniq()
      .compact()
      .value();
    return !_.isEmpty(items)? items: null;
  }

  getDirectors() {
    let parts = this.findInnerTexts(this.selectors.directors);
    let items = _.chain(parts)
      .map(_.trim)
      .filter(v => { return _.startsWith(v, 'Director'); })
      .first()
      .replace(/^Directors?\s*:?/, '')
      .replace(/\|.*$/, '')
      .trim()
      .split(',')
      .map(_.trim)
      .uniq()
      .compact()
      .value();
    return !_.isEmpty(items)? items: null;
  }

  getContentRating() {
    let str = this.findInnerText(this.selectors.contentRating);
    let parts = _.chain(str)
      .trim()
      .split('|')
      .map(_.trim)
      .compact()
      .value();

    let retval = _.nth(parts, 0);
    let rating = !_.isEmpty(retval)? retval: null;

    let validRatings = this.constructor.ValidContentRatings;
    if (!_.isEmpty(validRatings)) {
      let validRating = _.find(validRatings, v => {
        return (_.toLower(v.label) === _.toLower(rating));
      });
      rating = _.get(validRating, 'value') || null;
    }

    return rating;
  }

  getStarRating() {
    let str = this.findInnerText(this.selectors.starRating);
    return str? parseFloat(str): null;
  }

  getMetaScore() {
    let str = this.findInnerText(this.selectors.metascore);
    return str? parseFloat(str): null;
  }

  getGenres() {
    let str = this.findInnerText(this.selectors.genre);
    let parts = _.chain(str)
      .trim()
      .split('|')
      .map(_.trim)
      .compact()
      .value();

    let genreStr = _.nth(parts, -2);
    let genreItems = _.chain(genreStr)
      .trim()
      .split(',')
      .map(_.trim)
      .uniq()
      .compact()
      .value();

    return !_.isEmpty(genreItems)? genreItems: null;
  }


  // DOM helpers

  findElement(selectors) {
    return this.doc.querySelector(selectors);
  }

  findElements(selectors) {
    let elements = [];
    let elNodeList = this.doc.querySelectorAll(selectors);
    if (elNodeList && elNodeList.length > 0) {
      _.each(elNodeList, v => {
        elements.push(v);
      });
    }
    return elements;
  }

  findInnerText(selectors) {
    let str = null;
    let el = this.findElement(selectors);
    try {
      if (el) {
        str = _.trim(el.textContent);
      }
    } catch (ex) {}
    return str;
  }

  findInnerTexts(selectors) {
    let elements = this.findElements(selectors);
    return _.map(elements, el => {
      return _.trim(el.textContent);
    });
  }

  findAttr(selectors, attrName) {
    let str = null;
    let el = this.findElement(selectors);
    try {
      if (el) {
        str = _.trim(el.getAttribute(attrName));
      }
    } catch (ex) {}
    return str;
  }

  findAttrs(selectors, attrName) {
    let elements = this.findElements(selectors);
    return _.map(elements, el => {
      return _.trim(el.getAttribute(attrName));
    });
  }

}


